import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { ProductForm } from "@/components/products/ProductForm";
import { ProductOrders } from "@/components/products/ProductOrders";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeleteProduct, useProduct, useUpdateProduct } from "@/hooks/use-products";
import type { ProductStatus } from "@/lib/enums";
import { formatCurrency, formatDate } from "@/lib/format";

export function ProductDetailPage() {
  const { productId } = useParams({ strict: false }) as { productId: string };
  const navigate = useNavigate();
  const { data: product, isLoading, isError, error, refetch } = useProduct(productId);
  const updateProduct = useUpdateProduct(productId);
  const deleteProduct = useDeleteProduct();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load product"} onRetry={() => refetch()} />
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Product not found.</p>
        <Link to="/dashboard/products" className="text-sm text-primary hover:underline mt-2 block">
          Back to products
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to="/dashboard/products"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Products
        </Link>
        <span className="text-muted-foreground">/</span>
        <h1 className="text-2xl font-bold tracking-tight">{product.name}</h1>
        {!editing && <StatusBadge status={product.status} />}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Details</CardTitle>
          {!editing && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {editing ? (
            <ProductForm
              defaultValues={{
                name: product.name,
                sku: product.sku,
                priceCents: product.priceCents,
                description: product.description,
                status: product.status as ProductStatus,
              }}
              onSubmit={(result) =>
                updateProduct.mutate(result, { onSuccess: () => setEditing(false) })
              }
              onCancel={() => setEditing(false)}
              isSaving={updateProduct.isPending}
            />
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">SKU</span>
                  <p className="mt-1 font-mono text-xs">{product.sku}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Price</span>
                  <p className="mt-1">{formatCurrency(product.priceCents)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <div className="mt-1">
                    <StatusBadge status={product.status} />
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Created</span>
                  <p className="mt-1">{formatDate(product.createdAt)}</p>
                </div>
              </div>
              {product.description && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Description</span>
                  <p className="mt-1">{product.description}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <ProductOrders lineItems={product.lineItems} />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete product"
        description={`Delete ${product.name}? Products referenced by existing orders cannot be deleted.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteProduct.isPending}
        onConfirm={() =>
          deleteProduct.mutate(product.id, {
            onSuccess: () => navigate({ to: "/dashboard/products" }),
          })
        }
      />
    </div>
  );
}
